import React from 'react';
import { 
  FileText, CreditCard, SearchCheck, Award, 
  GraduationCap, LayoutDashboard, ArrowRight, CheckCircle2,
  AlertCircle, ShieldCheck
} from 'lucide-react';

export const JourneyWorkflow = ({ setActiveView }) => {
  const steps = [
    {
      num: "01",
      title: "Online Application Dossier",
      description: "Complete biographical & academic profile and upload qualifying degrees, CV, and concept note. Application initiates screening review.",
      actionLabel: "Start Application",
      viewTarget: "apply",
      icon: FileText,
      badge: "Stage 1: Application",
      cardClass: "translucent-card-blue-tint",
      iconBoxClass: "bg-blue-600/10 text-blue-900 border-blue-200/80 group-hover:bg-[#0B2644] group-hover:text-white",
      badgeClass: "bg-blue-100/80 text-blue-950 border-blue-200"
    },
    {
      num: "02",
      title: "Paystack Processing Fee",
      description: "Secure settlement of statutory ₦25,000 application & credential verification fee via Paystack (Debit Card, Transfer, USSD).",
      actionLabel: "Fee Gateway",
      viewTarget: "apply",
      icon: CreditCard,
      badge: "Fee Clearance",
      cardClass: "translucent-card-emerald-tint",
      iconBoxClass: "bg-emerald-600/10 text-emerald-900 border-emerald-200/80 group-hover:bg-[#0B2644] group-hover:text-white",
      badgeClass: "bg-emerald-100/80 text-emerald-950 border-emerald-200"
    },
    {
      num: "03",
      title: "Admissions Screening",
      description: "Postgraduate board evaluates credentials, transcript authenticity, and professional pedigree. Offers are extended strictly on merit.",
      actionLabel: "Screening Queue",
      viewTarget: "screening",
      icon: SearchCheck,
      badge: "Merit Evaluation",
      cardClass: "translucent-card-indigo-tint",
      iconBoxClass: "bg-indigo-600/10 text-indigo-900 border-indigo-200/80 group-hover:bg-[#0B2644] group-hover:text-white",
      badgeClass: "bg-indigo-100/80 text-indigo-950 border-indigo-200"
    },
    {
      num: "04",
      title: "Provisional Admission Offer",
      description: "Screened candidates receive official signed Provisional Admission Letter with QR verification and acceptance conditions.",
      actionLabel: "Sample Offer",
      viewTarget: "offer-letter",
      icon: Award,
      badge: "Senate Ratification",
      cardClass: "translucent-card-gold-tint",
      iconBoxClass: "bg-amber-600/15 text-[#7A470E] border-amber-300/80 group-hover:bg-[#7A470E] group-hover:text-white",
      badgeClass: "bg-amber-100/80 text-[#5C3306] border-amber-300"
    },
    {
      num: "05",
      title: "Acceptance & Enrolment",
      description: "Confirm acceptance fee (₦50,000), register accredited semester modules, and receive official university matriculation number & digital ID.",
      actionLabel: "Enrolment Portal",
      viewTarget: "enrolment",
      icon: GraduationCap,
      badge: "Matric & Digital ID",
      cardClass: "translucent-card-cyan-tint",
      iconBoxClass: "bg-cyan-600/10 text-cyan-900 border-cyan-200/80 group-hover:bg-[#0B2644] group-hover:text-white",
      badgeClass: "bg-cyan-100/80 text-cyan-950 border-cyan-200"
    },
    {
      num: "06",
      title: "Executive LMS Induction",
      description: "Enter executive digital learning portal, study modules, doctoral colloquiums, and live interactive hybrid classes.",
      actionLabel: "Launch LMS",
      viewTarget: "lms-student",
      icon: LayoutDashboard,
      badge: "Active Scholar",
      cardClass: "translucent-card-amber-navy",
      iconBoxClass: "bg-[#7A470E]/15 text-[#7A470E] border-[#7A470E]/40 group-hover:bg-[#0B2644] group-hover:text-white",
      badgeClass: "bg-amber-100/90 text-[#7A470E] border-amber-300 font-black"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-[#F3F7FA] via-[#FAF6F0]/70 to-[#EDF3F9] border-b-2 border-slate-200 relative overflow-hidden">
      {/* Ambient background glows for vivid translucency and backdrop-blur */}
      <div className="absolute top-10 left-10 w-96 h-96 bg-[#7A470E]/12 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0B2644]/12 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-gradient-to-r from-[#B87A28]/10 via-[#0B2644]/8 to-[#7A470E]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 subtle-grid-dark opacity-15 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-white/80 px-3 py-1 rounded-lg border border-[#7A470E]/40 mb-3 font-sc shadow-2xs backdrop-blur-md">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#7A470E]" />
            <span>End-to-End Executive Pathway</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-[#0B2644] font-sc">
            From Application to Executive LMS
          </h2>
          <p className="mt-4 text-base text-slate-600">
            A frictionless, world-class digital lifecycle engineered for executive convenience and transparency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div 
                key={idx}
                className={`${s.cardClass} rounded-3xl p-6 shadow-sm hover:border-[#7A470E] hover:shadow-xl transition-all flex flex-col justify-between group hover:scale-[1.02]`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl font-black font-sc text-[#7A470E]/70 group-hover:text-[#7A470E] transition-colors">
                      {s.num}
                    </span>
                    <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border font-sc ${s.badgeClass}`}>
                      {s.badge}
                    </span>
                  </div>

                  <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center transition-colors mb-3 shadow-xs ${s.iconBoxClass}`}>
                    <Icon className="w-6 h-6" />
                  </div>

                  <h3 className="text-xl font-black text-slate-900 font-sc group-hover:text-[#0B2644] transition-colors">
                    {s.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                    {s.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-200/80 flex items-center justify-between">
                  <button
                    onClick={() => setActiveView(s.viewTarget)}
                    className="text-xs font-bold text-[#7A470E] hover:text-[#5C3306] transition-colors flex items-center space-x-1 cursor-pointer font-sc"
                  >
                    <span>{s.actionLabel}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[11px] text-slate-400 font-mono">
                    Phase {idx + 1}/6
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Academic Governance & Statutory Disclaimer Banner */}
        <div className="mt-12 p-6 rounded-3xl bg-white/90 border-2 border-amber-300 shadow-sm backdrop-blur-md max-w-4xl mx-auto">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 rounded-2xl bg-amber-100 text-[#7A470E] flex items-center justify-center shrink-0 mt-0.5 border border-amber-300">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="space-y-1.5 text-xs text-slate-700">
              <div className="flex items-center space-x-2">
                <span className="font-extrabold font-sc uppercase tracking-wider text-[#0B2644] text-xs">
                  Academic Governance Notice: Application Does Not Guarantee Admission
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-[#7A470E] font-sc">
                  Postgraduate Regulations
                </span>
              </div>
              <p className="leading-relaxed text-slate-700 font-medium">
                Prospective applicants are formally advised that purchasing an application form, paying the ₦25,000 processing fee, and submitting academic credentials does <strong>NOT</strong> constitute an offer or guarantee of admission. Offers of provisional admission are extended strictly upon successful verification of qualifying degrees, academic transcript audit, departmental interview/screening, and formal ratification by the University Senate in accordance with National Universities Commission (NUC) guidelines and departmental quota capacity.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
